<template lang="pug">
  .b-crm-lead-detail-ui
    h2._title {{ lead.name }}
    ._panels
      ._details
        ._top-row
          //- на месте номера заявки: напоминания себе по этой заявке, в том
          //- же виде, что кнопка «Напомнить» в деталке задачи (TaskReminder)
          button._remind(type="button" @click="$emit('openReminders')")
            Icon(name="notifications" colored pointer)
            | Напомнить
          span._created от {{ lead.createdAt }}
        ._top-row
          span._funnel-label Воронка
          BaseStatus(
            :items="columns"
            :value="lead.columnId"
            :editable="!!columns.length"
            @change="$emit('changeStage', $event)"
            )
        //- вне сворачиваемых «Деталей»: причина отказа объясняет плашку над
        //- ней, и прятать её от глаз вместе с остальным незачем
        ._reason-row(v-if="lead.rejectionReason")
          span._reason-label Причина отказа
          span._reason-value {{ lead.rejectionReason }}
        ._details-header
          button._details-header-top(type="button" @click="toggleDetails")
            span._details-title Детали
            Icon._details-icon(name="round-app" pointer :class="{ 'opened': detailsOpened }")
          ._details-line(v-if="!detailsOpened")
        ._details-content(v-if="detailsOpened")
          ._author-row
            //- менеджера у заявки может не быть: поле manager обнуляемое
            UserTile._author(v-if="lead.author" :user="lead.author" :size="40")
            span._author-empty(v-else) Ответственный не назначен
            button._edit(
              v-if="users.length"
              type="button"
              title="Сменить ответственного"
              @click="$emit('changeManager')"
              )
              Icon(name="edit" pointer colored)
          ._divider
          ._fields
            ._field
              span._field-label Клиент
              ._field-value
                span {{ lead.client }}
                //- правка самого клиента, а не выбор другого: телефон и почта
                //- ниже — его же поля и обновляются вместе с ним. Сменить
                //- клиента у заявки нельзя вовсе — он задаётся при создании
                button._edit(
                  v-if="lead.client && canEditClient"
                  type="button"
                  title="Редактировать клиента"
                  @click="$emit('editClient')"
                  )
                  Icon(name="edit" pointer colored)
            ._field
              span._field-label Телефон
              span._field-value {{ lead.phone }}
            ._field
              span._field-label Почта
              span._field-value.-muted {{ lead.email || 'не указано' }}
            ._field
              span._field-label День рождения
              span._field-value.-muted {{ lead.birthday || 'не указан' }}
          ._divider
          //- Тип услуги, суть обращения и вложения: под данными клиента и до
          //- списка проектов. Типов услуг у воронки может не быть вовсе —
          //- тогда и выбирать не из чего, поле не показываем
          ._field(v-if="serviceTypes.length")
            span._field-label Тип услуги
            BaseStatus(
              :items="serviceTypes"
              :value="lead.serviceTypeId"
              editable
              emptyLabel="Не указан"
              @change="$emit('changeServiceType', $event)"
              )
          ._block
            span._field-label Суть обращения клиента
            BaseTextarea(
              v-model="description"
              label="Опишите, с чем обратился клиент"
              )
            Btn._description-save(
              v-if="descriptionChanged"
              xs blue
              type="button"
              @click="$emit('saveDescription', description.trim())"
              ) Сохранить
          ._block
            span._field-label Файлы
            FileList(v-if="fileItems.length" :files="fileItems" @remove="removeFile")
            CrmFileUploadButton._upload(
              ref="upload"
              :files="lead.files"
              label="Добавить файл"
              @change="$emit('changeFiles', $event)"
              @uploadingChange="uploading = $event"
              )
        ._divider(v-if="detailsOpened")
        //- проекты не прячутся в «Детали»: они видны при любом состоянии блока
        ._projects(v-if="lead.projects.length")
          ._project(v-for="project in lead.projects" :key="project.id")
            //- project-stages, а не project-detail: у деталки проекта нет
            //- собственного содержимого, весь проект открывается со списка задач
            Link._project-name(:to="{ name: 'project-stages', params: { projectId: project.id } }") {{ project.name }}
            BaseStatus._project-status(:items="project.statusItems" :value="project.statusValue")
        Btn._add-project(blue lg type="button" @click="$emit('addProject')") Добавить проект
      //- Ленту событий рендерит родитель: это контейнер со своими запросами.
      //- Здесь только место под неё — сразу под блоком с основной информацией
      slot(name="timeline")
</template>

<script>
import BaseStatus from '@/components/BaseStatus.vue';
import UserTile from '@/components/UserTile.vue';
import BaseTextarea from '@/components/BaseTextarea.vue';
import FileList from '@/components/FileList.vue';
import CrmFileUploadButton from '@/components/crm/CrmFileUploadButton.vue';

export default {
  name: 'CrmLeadDetailUi',
  components: {
    BaseStatus, UserTile, BaseTextarea, FileList, CrmFileUploadButton,
  },
  // UserTile reads its "автор" caption from an injected `positions`
  // function keyed by user id, rather than from a prop it exposes directly
  provide() {
    return {
      positions: () => (this.lead.author ? { [this.lead.author.id]: 'ответственный' } : {}),
    };
  },
  props: {
    lead: {
      type: Object,
      required: true,
    },
    // колонки доски: этапы воронки плюс завершающие — заявку отсюда можно и
    // перевести на другой этап, и закрыть
    columns: {
      type: Array,
      default: () => [],
    },
    // кого можно назначить ответственным
    users: {
      type: Array,
      default: () => [],
    },
    // типы услуг воронки; у них нет своего цвета, поэтому BaseStatus рисует
    // все плашки одинаковыми
    serviceTypes: {
      type: Array,
      default: () => [],
    },
    // клиента заявки правит руководящий состав компании и автор клиента
    canEditClient: Boolean,
  },
  data() {
    return {
      // custom collapse toggle for the "Детали" block (own header + content
      // below, not BaseAccordion — its content wrapper only ever toggles
      // display:none/block, which fought any layout override we tried to
      // put on it) — the divider below reuses this same flag so it isn't left
      // dangling under a collapsed block; the projects list stays visible.
      // Свёрнут при открытии и сворачивается на любой ширине: деталка живёт
      // в узкой боковой шторке, где ширина окна ничего не решает, а первой
      // на глаза должна попадаться лента событий
      detailsOpened: false,
      // черновик описания: в отличие от типа услуги и вложений он не
      // сохраняется сам, а ждёт кнопки — см. descriptionChanged
      description: this.lead.description ?? '',
      // вложения, которые ещё летят на сервер; ими управляет
      // CrmFileUploadButton (uploading.sync)
      uploading: [],
    };
  },
  watch: {
    // описание могло смениться и снаружи: после сохранения заявка
    // перечитывается, а её ещё и правят из других мест
    'lead.description': function onDescription(value) {
      this.description = value ?? '';
    },
  },
  computed: {
    descriptionChanged() {
      return this.description.trim() !== (this.lead.description ?? '');
    },
    /** Обе половины вложений в одном списке для FileList. */
    fileItems() {
      return [
        ...this.lead.files.map(file => ({ id: file.id, file, removable: true })),
        ...this.uploading,
      ];
    },
  },
  methods: {
    toggleDetails() {
      this.detailsOpened = !this.detailsOpened;
    },
    /**
     * Недогруженный файл просто отменяем — это ещё не удаление, спрашивать не
     * о чем. За уже прикреплённый отвечает родитель: он спросит подтверждение
     * и пришлёт новый набор обратно
     */
    removeFile(item) {
      if (item.controller) {
        this.$refs.upload.removeItem(item);
        return;
      }

      this.$emit('removeFile', item.file);
    },
  },
};
</script>

<style scoped lang="scss">
.crm-lead-detail-ui {
  // Деталка занимает боковую шторку целиком: ширину и фон задаёт шторка
  // (см. openLeadDrawer в CrmBoardApi), отступы — свои
  box-sizing: border-box;
  padding: 8px 24px 24px;

  &__title {
    font-weight: 700;
    font-size: 18px;
    color: var(--color-dark-gray);
    margin-bottom: 24px;
  }

  // Одна колонка: комментарии всегда под блоком с основной информацией.
  // Высоту никто не ограничивает — прокручивается шторка целиком, а не
  // отдельные панели внутри неё
  &__panels {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__details {
    background: var(--color-white);
    border-radius: 8px;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__top-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  &__remind {
    display: flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: none;
    padding: 0;
    color: #788CA1;
    font-size: 15px;
    text-decoration: underline dotted;
    text-underline-offset: 3px;
    cursor: pointer;

    &:hover {
      text-decoration: none;
    }

    svg {
      color: #FFC531;
      height: 20px;
      width: auto;
    }
  }

  &__created, &__created-date {
    font-size: 14px;
    color: var(--color-middle-gray);
  }

  &__funnel-label {
    font-weight: 500;
    font-size: 16px;
    color: var(--color-accent);
  }

  // Не __top-row: причина бывает длинной и переносится на несколько строк, а
  // там align-items: center — подпись повисала бы по центру этих строк
  &__reason-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
  }

  &__reason-label {
    // подпись короткая и известная, переносить её незачем — сжимается пусть
    // значение
    flex-shrink: 0;
    font-size: 14px;
    color: var(--color-middle-gray);
  }

  &__reason-value {
    // без min-width флекс-элемент не сжимается меньше своего содержимого и
    // длинная причина распирала бы строку
    min-width: 0;
    font-weight: 500;
    font-size: 14px;
    // тот же красный, что и у колонки «Отказ» (CLOSING_COLUMNS)
    color: #E21313;
    text-align: right;
    // причина — произвольный текст: длинное слово без пробелов рвём, иначе
    // оно вылезет за панель
    overflow-wrap: anywhere;
  }

  &__details-header {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__details-header-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    width: 100%;
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
  }

  &__details-title {
    color: #1E3147;
    font-size: 13px;
    font-weight: 500;
    line-height: 24px;
  }

  &__details-line {
    height: 1px;
    background: var(--color-purple);
  }

  &__details-icon {
    transform: rotate(180deg);
    transition: transform 0.2s ease;

    &.opened {
      transform: rotate(0deg);
    }
  }

  &__details-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__author-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  &__author {
    min-width: 0;
  }

  &__author-empty {
    font-size: 14px;
    color: var(--color-middle-gray);
  }

  &__divider {
    height: 1px;
    background: var(--color-purple);
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__field {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    font-size: 14px;
  }

  &__field-label {
    flex-shrink: 0;
    color: var(--color-light-dark);
  }

  &__field-value {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--color-dark-gray);
    text-align: right;

    &--muted {
      color: #788CA1;
    }
  }

  // Описание и вложения — не пары «подпись: значение», а блоки во всю
  // ширину: подпись сверху, содержимое под ней
  // align-items оставляем по умолчанию (stretch): FileList внутри сам
  // раскладывает плитки сеткой с переносом, но только если получил ширину —
  // с flex-start он сжимался по содержимому и плитки вставали в столбик
  &__block {
    display: flex;
    flex-direction: column;
    gap: 16px;
    font-size: 14px;
  }

  &__description-save {
    align-self: flex-end;
  }

  // кнопка шириной по содержимому: растянутая на весь блок она увела бы
  // подпись с плюсом в центр
  &__upload {
    align-self: flex-start;
  }

  &__edit {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: var(--color-middle-gray);
  }

  &__projects {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__project {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: #F8F9FC;
    border-radius: 6px;
    padding: 14px 16px;
  }

  &__project-name {
    font-size: 12px;
    color: var(--color-dark-gray);
  }

  // Плашка статуса проекта уже, чем обычная: строка проекта узкая.
  // Обращаемся к простому классу-зацепке BaseStatus, а не к его БЭМ-имени
  &__project ::v-deep .base-status {
    flex-shrink: 0;
    min-width: 100px;
  }

  &__add-project {
    align-self: flex-end;
  }

  @media (max-width: 768px) {
    padding: 8px 16px 24px;

    &__details {
      padding: 16px;
    }

    &__project {
      &-name {
        width: 100%;
      }

      flex-wrap: wrap;
    }
  }
}
</style>
